import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToastNotification } from '@/hooks';
import { getAccessToken } from '@/utils/auth';

import { ChangePasswordForm } from '@/components';
import { NavigationSource, PagesPaths } from '@/enums';
import { useMutation } from '@tanstack/react-query';
import { changePassword } from './passwordChange';

export interface ResetPasswordProps {
  isChangingPassword?: boolean;
}
export const ResetPassword: FC<ResetPasswordProps> = ({
  isChangingPassword,
}) => {
  const { t } = useTranslation('resetPassword');
  const navigate = useNavigate();
  const { showToast } = useToastNotification();
  const token = getAccessToken() || null;

  const resetPasswordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      if (isChangingPassword) {
        showToast({ severity: 'success', detail: t('successConfirmPassword') });
      }
      navigate(`${PagesPaths.Login}?from=${NavigationSource.ResetPassword}`);
    },
    onError: () => {
      showToast({ severity: 'error', detail: t('toastErrorText') });
    },
  });

  const handleFormSubmit = (newPassword: string, oldPassword?: string) => {
    if (oldPassword && token) {
      resetPasswordMutation.mutate({
        newPassword,
        oldPassword,
      });
    }
  };

  const submitButtonText = resetPasswordMutation.isPending
    ? t('loadingButton')
    : t('submitButton');

  return (
    <ChangePasswordForm
      onSubmit={handleFormSubmit}
      submitButtonText={submitButtonText}
      isLoading={resetPasswordMutation.isPending}
      isChangingPassword={isChangingPassword}
    />
  );
};
